import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Select } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useParams } from "react-router-dom";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import UserRoles from "../../../enums/UserRoles";
import { IEquipment, IUser } from "../index";

interface IParams {
  userId: string;
}

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (user?: IUser) => void;
  user: IUser | undefined;
}

interface IUserFormData {
  name: string;
  email: string;
  phone_number?: string;
  monthly_productive_hours?: number;
  hiring_model: "CORPORATE" | "SHAREHOLDER" | "CLT" | "CLT_TEMP";
  role: UserRoles;
  seniority: "JUNIOR" | "FULL" | "SENIOR";
  manager_id: string;
  admission_date: any;
  dismissal_date: any;
  select_equipment: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const EquipmentModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  user,
}) => {
  const { t } = useTranslation(["consultants"]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [equipments, setEquipments] = useState<IEquipment[]>();
  const { addToast } = useToast();
  const [form] = Form.useForm();
  const { userId } = useParams<IParams>();

  const isEditing = useMemo(() => !!user, [user]);

  useEffect(() => {
    api
      .get<IEquipment[]>("/equipments", {
        params: {
          status: "FREE",
        },
      })
      .then((response) => {
        setEquipments(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar os equipamento",
          description:
            "Occoreu um erro ao buscar os equipamento, tente novamente.",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCloseModal, loading, user]);

  const equipmentsOptions = useMemo(() => {
    if (!equipments) {
      return undefined;
    }

    return (
      equipments
        // eslint-disable-next-line no-nested-ternary
        .map((equip) => ({
          label: `${equip.equipment_type} - ${equip.equipment_description} - ${equip.equipment_id}`,
          value: equip.id,
        }))
    );
  }, [equipments, form]);

  const handleClose = useCallback(
    (usr?: IUser) => {
      form.resetFields(["select_equipment"]);
      handleCloseModal(usr);
    },
    [form, handleCloseModal, user]
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O usuário não foi encontrado";
          break;
        case 409:
          description = "O E-mail informado já é usado por outro usuário";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do usuário, tente novamente"
            : "Ocorreu um erro ao criar o usuário, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing ? "Erro na edição" : "Erro na criação",
        description,
      });
    },
    [addToast, isEditing]
  );

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      setLoading(true);
      const { select_equipment } = data;

      const payload = {
        equipament: select_equipment,
        statusEq: "IN USE",
        assignmentEq: user?.name,
        flag_equipment: 1,
      };

      try {
        const response = await api.patch(
          `/users/${userId}/consultant/equipment`,
          payload
        );
        addToast({
          type: "success",
          title: "Usuário alterado",
          description: "O usuário foi alterado com sucesso",
        });

        handleClose(response.data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [handleClose, addToast, userId, generateErrorToast]
  );
  return (
    <Modal
      title={t("modalAddEquipment.add")}
      visible={visible}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="select_equipment"
          label={t("equipments.title")}
          errors={validationErrors}
        >
          <Select
            allowClear
            defaultValue=""
            style={{ width: "100%" }}
            options={equipmentsOptions}
          />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default EquipmentModal;
